import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip, 
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  AddShoppingCart as AddToCartIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import EditItemDialog from './EditItemDialog';
import StorageItemView from './StorageItemView';

interface StorageItem {
  id: string;
  name: string;
  description: string;
  category: 'business' | 'office' | 'exhibition' | 'souvenirs' | 'work_tools';
  quantity: number;
  minQuantity: number;
  unit: string;
  location: string;

  supplier: string;
  savikaina?: number;
  pardavimoKaina?: number;
  creator: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface OfficeItemsProps {
  refreshTrigger: number;
  onAddToCart: (item: StorageItem, quantity: number) => void;
  viewMode: 'cards' | 'list';
}

const OfficeItems: React.FC<OfficeItemsProps> = ({ refreshTrigger, onAddToCart, viewMode }) => {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [allSuppliers, setAllSuppliers] = useState<string[]>([]);
  const [selectedQuantities, setSelectedQuantities] = useState<{ [key: string]: number }>({});
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<StorageItem | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<StorageItem | null>(null);

  useEffect(() => {
    fetchItems();
  }, [refreshTrigger, supplierFilter]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY('office')}?sortBy=${sortBy}&sortOrder=${sortOrder}&supplier=${supplierFilter}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setItems(data.items);
        if (supplierFilter === 'all') {
          const suppliers = Array.from(new Set(data.items.map((item: StorageItem) => item.supplier).filter(Boolean))).sort() as string[];
          setAllSuppliers(suppliers);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch items');
      }
    } catch (error) {
      console.error('Error fetching office items:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    setSelectedQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, Math.min(quantity, items.find(item => item.id === itemId)?.quantity || 1))
    }));
  };

  const handleAddToCart = (item: StorageItem) => {
    const quantity = selectedQuantities[item.id] || 1;
    onAddToCart(item, quantity);
    setSelectedQuantities(prev => {
      const newState = { ...prev };
      delete newState[item.id];
      return newState;
    });
  };

  const handleEditItem = (item: StorageItem) => {
    setItemToEdit(item);
    setEditItemOpen(true);
  };

  const handleItemUpdated = () => {
    fetchItems();
  };

  const handleDeleteItem = (item: StorageItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/storage/items/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to delete item');
      }

      fetchItems();
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  // Extract unique suppliers from current items
  const uniqueSuppliers = Array.from(new Set(items.map(item => item.supplier).filter(Boolean))).sort();

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search and Sort Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Ieškoti kanceliarinių prekių"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ minWidth: 250 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tiekėjas</InputLabel>
          <Select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            label="Tiekėjas"
          >
            <MenuItem value="all">Visi tiekėjai</MenuItem>
            {allSuppliers.map((supplier) => (
              <MenuItem key={supplier} value={supplier}>
                {supplier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={sortBy === 'name' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleSort('name')}
            startIcon={<SortIcon />}
          >
            Pavadinimas {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
          </Button>
          <Button
            variant={sortBy === 'quantity' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleSort('quantity')}
            startIcon={<SortIcon />}
          >
            Kiekis {sortBy === 'quantity' && (sortOrder === 'ASC' ? '↑' : '↓')}
          </Button>
        </Box>
      </Box>

      <StorageItemView
        items={filteredItems}
        viewMode={viewMode}
        selectedQuantities={selectedQuantities}
        onQuantityChange={handleQuantityChange}
        onAddToCart={handleAddToCart}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        canEdit={hasPermission('storage.edit')}
        emptyMessage="Nerasta kanceliarinių prekių"
      />

      {/* Edit Item Dialog */}
      <EditItemDialog
        open={editItemOpen}
        onClose={() => setEditItemOpen(false)}
        onItemUpdated={handleItemUpdated}
        item={itemToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Patvirtinti ištrynimą</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite ištrinti prekę "{itemToDelete?.name}"? 
            Šis veiksmas negrįžtamas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Atšaukti
          </Button>
          <Button 
            onClick={confirmDeleteItem}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Ištrinti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfficeItems;
